import { Box, Button, Dialog, DialogActions, DialogContent, Typography, useMediaQuery } from "@mui/material"
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useEffect, useMemo, useState } from "react";
import theme from "src/theme/light";
import { useProjetosHibridoStore } from "src/store/vendas/projetos/hibrido/hibrido.store";
import { MonthSelect } from "../dimensionados/containers/Configuracoes/components/MonthSelect";
import { addMonths, eachMonthOfInterval, format, subMonths } from "date-fns";
import { InputDecimal3 } from "src/framework/components/forms/inputs/InputDecimal3";
import { ptBR } from "date-fns/locale";
import { ProjetosProvider } from "src/providers/ProjetosProvider";

type ModalPotenciaProps = {
  open: boolean;
  onClose: () => void;
}

const capitalizeFirstLetter = (str: string) => {
  const [firstLetter, ...rest] = str;
  return `${firstLetter.toUpperCase()}${rest.join('')}`
}

const useStyles = makeStyles(() =>{
  return {
    monthSelect: {
      width: "100%"
    },
    calcularPotenciaButton: {
      backgroundColor: theme.palette.warning.dark,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.warning.dark,
      }
    }
  }
})

const MAX_MONTHS_INTERVAL = 12;
const projetosProvider = new ProjetosProvider()

export const ModalPotencia = ({ onClose, open }: ModalPotenciaProps) => {
  const classes = useStyles();
  const { changeModalsValue } = useProjetosHibridoStore(store => store.actions)
  const projetosHibridoStoreState = useProjetosHibridoStore(store => store.state.data)
  const projetosHibridoStoreActions = useProjetosHibridoStore(store => store.actions)
  
  const consumoDefault = useMemo(() => {
    return {
      consumo_data_inicial: projetosHibridoStoreState.consumo_data_inicial,
      consumo_data_final: projetosHibridoStoreState.consumo_data_final,
      consumo_meses: projetosHibridoStoreState.consumo_meses,
      potencia_desejada_kw: projetosHibridoStoreState.potencia_desejada_kw,
      potencia_desejada_w: projetosHibridoStoreState.potencia_desejada_w,
    };
  }, [])

  const rollbackValues = useCallback(()=>{
    projetosHibridoStoreActions.changeValue({
      consumo_meses: consumoDefault.consumo_meses,
      consumo_data_inicial: consumoDefault.consumo_data_inicial,
      consumo_data_final: consumoDefault.consumo_data_final,
      potencia_desejada_kw: consumoDefault.potencia_desejada_kw,
      potencia_desejada_w: consumoDefault.potencia_desejada_w,
    })
  },[consumoDefault])

  const [consumoChanged, setConsumoChanged] = useState(false)
  const [potenciaCalculadaKw, setPotenciaCalculadaKw] = useState(projetosHibridoStoreState.modo_potencia === 'calculo' ? Number(projetosHibridoStoreState.potencia_desejada_kw || 0) : 0)

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterButton = isMobile ? Box : DialogActions;

  const validateAndSave = useCallback(()=>{
    projetosHibridoStoreActions.changeValue({ modo_potencia: 'calculo', potencia_desejada_kw: potenciaCalculadaKw, potencia_desejada_w: Number((potenciaCalculadaKw || 0) * 1000) })
    changeModalsValue({ potencia_desejada: false })
  },[changeModalsValue, potenciaCalculadaKw])

  useEffect(() => {
    if (!projetosHibridoStoreState.consumo_data_inicial || !projetosHibridoStoreState.consumo_data_final) return

    try {
      const interval = eachMonthOfInterval({
        start: projetosHibridoStoreState.consumo_data_inicial,
        end: projetosHibridoStoreState.consumo_data_final,
      })


      const consumo_meses = interval.map(day => {
      const oldMonth = projetosHibridoStoreState.consumo_meses.find(item => item.day === day.toISOString())

      return {
        day: day.toISOString(),
        value: oldMonth?.value || 0
      }
    })

    projetosHibridoStoreActions.changeValue({ consumo_meses })
    } catch (error) {
      // Suprime o erro de intervalo de datas...
    }
  },[projetosHibridoStoreState.consumo_data_final, projetosHibridoStoreState.consumo_data_inicial])


  const handleChangeDate = useCallback((date: Date | null, from: 'start' | 'end')=>{
    const startDate = from === 'start' ? date : projetosHibridoStoreState.consumo_data_inicial
    const endDate = from === 'end' ? date : projetosHibridoStoreState.consumo_data_final

    if (!startDate || !endDate) return;

    const interval = eachMonthOfInterval({
      start: startDate,
      end: endDate,
    })

    setConsumoChanged(true)

    if (interval.length > MAX_MONTHS_INTERVAL) {
      if (from === 'start') {
        projetosHibridoStoreActions.changeValue({ consumo_data_inicial: date, consumo_data_final: addMonths(new Date(startDate), 11) })
        return
      }

      if (from === 'end') {
        projetosHibridoStoreActions.changeValue({ consumo_data_inicial: subMonths(new Date(endDate), 11), consumo_data_final: date })
        return
      }
    }

    if (from === 'end') {
      projetosHibridoStoreActions.changeValue({ consumo_data_final: date || undefined })
    }

    if (from === 'start') {
      projetosHibridoStoreActions.changeValue({ consumo_data_inicial: date || undefined })
    }
  }, [projetosHibridoStoreState])

  const changeConsumo = useCallback((day: string, v: number) => {
    projetosHibridoStoreActions.changeValue({
      consumo_meses: projetosHibridoStoreState.consumo_meses.map(item => {
        if (item.day === day) return { ...item, value: v }
        return item
      })
    })
    
    setConsumoChanged(true)
  },[projetosHibridoStoreState.consumo_meses])

  const calcularPotenciaDesabilitado = useMemo(() => {
    if (!projetosHibridoStoreState.latitude) return true;
    if (!projetosHibridoStoreState.longitude) return true;
    return projetosHibridoStoreState.consumo_meses.some(item => Number(item.value || 0) === 0)
  }, [projetosHibridoStoreState.consumo_meses, projetosHibridoStoreState.latitude, projetosHibridoStoreState.longitude])

  const calcularPotencia = useCallback(()=>{
    projetosProvider.calcularPotencia({
      consumo_meses: projetosHibridoStoreState.consumo_meses.map(item => Number(item.value || 0)),
      fator_perda: 15,
      latitude: projetosHibridoStoreState.latitude,
      longitude: projetosHibridoStoreState.longitude,
    }).then(res => {
      setPotenciaCalculadaKw(res.data.potencia)
      setConsumoChanged(false)
    })
  },[projetosHibridoStoreState.latitude, projetosHibridoStoreState.longitude, projetosHibridoStoreState.consumo_meses])

  return (
    <Dialog open={open} onClose={(event, reason) => {
      if (reason === 'backdropClick') return ;
      onClose();
    }} maxWidth="xl" className="modal-potencia-hibrido">
      <DialogContent>
        <Box className="row" mb={5}>
          <Box className="X12 M6">
            <MonthSelect
              label="* Do mês"
              selected={projetosHibridoStoreState.consumo_data_inicial}
              onChange={(date) => {
                handleChangeDate(date, 'start')
              }}
              wrapperClassName={classes.monthSelect}
              maxDate={projetosHibridoStoreState.consumo_data_final ? subMonths(projetosHibridoStoreState.consumo_data_final, 1) : new Date()}
            />
          </Box>
          <Box className="X12 M6">
            <MonthSelect
              label="* Até o mês"
              wrapperClassName={classes.monthSelect}
              minDate={projetosHibridoStoreState.consumo_data_inicial ? addMonths(projetosHibridoStoreState.consumo_data_inicial, 1) : null}
              selected={projetosHibridoStoreState.consumo_data_final}
              onChange={(date) => {
                handleChangeDate(date, 'end')
              }}
              maxDate={new Date()}
            />
          </Box>
        </Box>

        <Typography align="left" variant='subtitle1'>Preencha o consumo em kWh dos meses selecionados</Typography>

        <Box display='flex' className="row" flexWrap="wrap">
          {projetosHibridoStoreState.consumo_meses.map(item => (
            <Box className="X6 M2">
              <InputDecimal3
                label={`* ${capitalizeFirstLetter(format(new Date(item.day), "MMM/yyyy", { locale: ptBR }))}`}
                margin="normal"
                maxLength={21}
                decimal={2}
                placeholder="Consumo (kWh)"
                allowNegative={false}
                value={item.value || ""}
                onChange={(event: any) => {
                  changeConsumo(item.day, event.target.value)
                }}
              />
            </Box>
          ))}
        </Box>


        <Box style={isMobile ? { display: 'flex', flexDirection: 'column-reverse' } : {}}>
          <Box className='row'>
            <Box className="X4"/>
            <Box className="X4"/>
            <Box className="X12 M4">
              <InputDecimal3
                label={`Potência desejada`}
                margin="normal"
                maxLength={21}
                decimal={2}
                sufix="kWp"
                value={potenciaCalculadaKw}
                disabled
              />
            </Box>
          </Box>
          <Box className='row' alignItems={'center'}>
            <Box className="X12 M8" display="flex" justifyContent={"flex-end"} style={isMobile ? {marginBottom: 5, textAlign: 'left'} : {}}>
              {!!(consumoChanged && potenciaCalculadaKw) && (
                <small style={{ color: theme.palette.error.main }}>Atenção! Os valores foram alterados. Clique no botão Calcular Potência para recalcular.</small>
              )}
            </Box>
            <Box className="X12 M4">
              <Button 
                fullWidth 
                disabled={calcularPotenciaDesabilitado}
                onClick={calcularPotencia} 
                color="inherit" 
                variant="contained" 
                className={classes.calcularPotenciaButton}
                style={isMobile ? { margin: '10px 0px' } : {}}
              >Calcular Potência</Button>
            </Box>
          </Box>
          <Box className='row'>
            <Box className="X4"/>
            <Box className="X4"/>
            <Box className="X4"/>
          </Box>
        </Box>

      </DialogContent>
      <FooterButton className="footer-modal-potencia">
        <Button
          onClick={() => {
            rollbackValues()
            changeModalsValue({ potencia_desejada: false })
          }}
          color="secondary"
          variant={isMobile ? "outlined" : "text"}
          fullWidth={isMobile}
        >
          Cancelar
        </Button>
        <Button
          onClick={validateAndSave}
          disabled={calcularPotenciaDesabilitado || consumoChanged || (potenciaCalculadaKw === 0 && projetosHibridoStoreState.modo_potencia === 'manual')}
          disableElevation
          fullWidth={isMobile}
          color="primary"
          variant="contained"
          style={isMobile ? {marginTop: 15} : {}}
        >
          Salvar
        </Button>
      </FooterButton>
    </Dialog>
  );
}